import { useState, useCallback } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Response type for the onboarding API endpoint
 */
type Response = {
  url?: string
  message: string
  onboarded: boolean
  account_status: PaymentProviderStatus
}

/**
 * Account details for payment provider
 */
export type AccountDetails = {
  message: string
  verified: boolean
  onboarded: boolean
  account:
    | string
    | {
        id: string
        object: string
        business_profile: {
          mcc: string
          name: string
          support_address: {
            city: string
            country: string
            line1: string
            line2: any | null
            postal_code: string
            state: string
          }
          support_email: any | null
          support_phone: string
          support_url: any | null
          url: string
        }
        capabilities: {
          card_payments: string
          transfers: string
          [key: string]: string
        }
        charges_enabled: boolean
        country: string
        default_currency: string
        details_submitted: boolean
        email: string
        payouts_enabled: boolean
        type: string
      }
}

/**
 * Account details for SportsPay payment provider
 */
export type SportsPayAccountDetails = {
  message: string
  verified: boolean
  onboarded: boolean
  account: {
    id: string
    // SportsPay specific account details
    // This is a placeholder and should be updated with actual fields
    [key: string]: any
  }
}

/**
 * Error type for payment providers
 */
export type ProviderError = {
  message: string
  code?: string
  type?: string
}

export enum PaymentProviderStatus {
  ERROR = 'error',
  INCOMPLETE = 'incomplete',
  IN_REVIEW = 'in-review',
  COMPLETED = 'completed',
}

export enum PaymentProviderId {
  SPORTSPAY = '2',
  STRIPE = '1',
}

/**
 * Custom hook to manage payment provider connections
 */
const usePaymentProviderConnection = () => {
  // Payment Provider states
  const [loading, setLoading] = useState(true)
  const [link, setLink] = useState<string | null>(null)
  const [isOnboarded, setIsOnboarded] = useState(false)
  const [accountStatus, setAccountStatus] =
    useState<PaymentProviderStatus | null>(null)
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(
    null
  )
  const [error, setError] = useState<boolean>(false)
  const [errorDetails, setErrorDetails] = useState<ProviderError | null>(null)

  /**
   * Updates account information to the payment provider
   * @param code - The code to post
   * @param status - The status to post
   * @param accountId - The account ID to post to
   * @param paymentProviderId - The payment provider ID to post to
   * @returns 200
   */
  const putAccountInfo = async (
    code: string,
    status: PaymentProviderStatus,
    accountId: string,
    paymentProviderId: PaymentProviderId
  ): Promise<Response | null> => {
    try {
      const response: Response = await req(
        `/account/${accountId}/payment_provider/onboarding`,
        {
          method: 'PUT',
          body: JSON.stringify({
            sportspay_merchant_id: code,
            sportspay_account_status: status,
            payment_provider_type_id: paymentProviderId,
          }),
        }
      )
      return response
    } catch (e: any) {
      console.error('Error on Put Account Info', e)
      return null
    }
  }

  /**
   * Updates account information to the payment provider for Stripe onboarding
   * @param accountId - The account ID to post to
   * @param paymentProviderId - The payment provider ID to post to
   * @returns 200
   */
  const putAccountInfoForStripeOnboarding = async (
    accountId: string,
    paymentProviderId: PaymentProviderId
  ): Promise<Response | null> => {
    try {
      const response: Response = await req(
        `/account/${accountId}/payment_provider/onboarding`,
        {
          method: 'PUT',
          body: JSON.stringify({
            payment_provider_type_id: paymentProviderId,
          }),
        }
      )
      return response
    } catch (e: any) {
      console.error('Error on Put Account Info', e)
      return null
    }
  }

  /**
   * Fetches Payment Provider URL
   * @param accountId - The account ID to fetch information for
   * @returns Response containing onboarding status and URLs
   */
  const fetchPaymentProviderUrl = async (
    accountId: string,
    providerId: string
  ): Promise<Response> => {
    try {
      // /account/{accountid}/payment_provider/1/url
      const response: Response = await req(
        `/account/${accountId}/payment_provider/${providerId}/url`
      )
      return response
    } catch (e: any) {
      throw new Error(JSON.stringify(e))
    }
  }

  /**
   * Fetches connected account information for both payment providers
   * @param accountId - The account ID to fetch information for
   * @returns Response containing onboarding status and URLs
   */
  const fetchPaymentProvider = async (accountId: string): Promise<Response> => {
    try {
      const response: Response = await req(
        `/account/${accountId}/payment_provider/onboarding`
      )
      return response
    } catch (e: any) {
      throw new Error(JSON.stringify(e))
    }
  }

  /**
   * Fetches account details
   * @param accountId - The account ID to fetch details for
   * @returns account details
   */
  const fetchAccountInfo = async (
    accountId: string
  ): Promise<AccountDetails> => {
    try {
      const response: AccountDetails = await req(
        `/account/${accountId}/payment_provider/details`
      )
      return response
    } catch (e: any) {
      throw new Error(JSON.stringify(e))
    }
  }

  /**
   * Handles error parsing and setting for payment providers
   * @param error - The error to parse
   * @param setError - Function to set error state
   * @param setErrorDetails - Function to set error details
   */
  const handleProviderError = (
    _error: any,
    _setError: (value: boolean) => void,
    _setErrorDetails: (value: ProviderError | null) => void
  ) => {
    _setError(true)
    try {
      if (typeof _error.message === 'string') {
        const parsedError = JSON.parse(_error.message)
        _setErrorDetails({
          message: parsedError.message || 'Unknown Error Occurred',
          code: parsedError.code,
          type: parsedError.type,
        })
      } else {
        _setErrorDetails({
          message: 'Failed To Connect To Payment Provider',
        })
      }
    } catch (parseError) {
      _setErrorDetails({
        message: _error.message || 'Failed To Connect To Payment Provider',
      })
    }
  }

  /**
   * Fetches data for both payment providers
   * @param accountId - The account ID to fetch data for
   */
  const fetchPaymentProvidersData = useCallback(async (accountId: string) => {
    if (!accountId) {
      setLoading(false)
      return
    }

    // Reset error states before fetching
    setError(false)
    setErrorDetails(null)
    setLoading(true)

    try {
      const response = await fetchPaymentProvider(accountId)

      setAccountStatus(response.account_status)

      // Handle Onboarding
      if (!response.onboarded) {
        setLink(response.url || null)
        setIsOnboarded(false)
      } else {
        setIsOnboarded(true)
        try {
          const details = await fetchAccountInfo(accountId)
          setAccountDetails(details)
        } catch (e: any) {
          console.error('Error fetching account details', e)
          handleProviderError(e, setError, setErrorDetails)
        }
      }
    } catch (e: any) {
      console.error('Error on Fetching Connected Account', e)
      // Handle errors for both providers
      handleProviderError(e, setError, setErrorDetails)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Resets all state values to their defaults
   */
  const resetState = useCallback(() => {
    // Reset States
    setLoading(true)
    setLink(null)
    setIsOnboarded(false)
    setAccountDetails(null)
    setError(false)
    setErrorDetails(null)
  }, [])

  return {
    // Payment Provider related states and functions
    accountStatus,
    loading,
    link,
    isOnboarded,
    accountDetails,
    error,
    errorDetails,

    // Functions
    fetchPaymentProvidersData,
    resetState,
    putAccountInfo,
    putAccountInfoForStripeOnboarding,
    fetchPaymentProviderUrl,
  }
}

export default usePaymentProviderConnection
